import { atom, selector, selectorFamily } from 'recoil';
import { APP_NOTIFICATION_CATEGORY_LABELS } from '../constants/appNotifications';
import { LiveStatusConfig } from '../types/liveStatusConfig';
import { NotificationConfig } from '../types/notificationConfig';

export const loadingNotificationSettingsState = atom({
  key: 'app.notificationSettings.loading',
  default: false,
});

export const notificationSettingsState = atom<NotificationConfig[] | undefined>({
  key: 'app.notificationSettings',
  default: undefined,
});

export const editingNotificationSettingsState = atom<NotificationConfig[] | undefined>({
  key: 'app.notificationSettings.editing',
  default: selector({
    key: 'app.notificationSettings.editing.initializer',
    get: ({ get }) => {
      return get(notificationSettingsState);
    },
  }),
});

export const updateNotificationSettingsState = atom<string | undefined>({
  key: 'app.notificationSettings.update',
  default: undefined,
});

export const filteredNotificationSettingsState = selector<NotificationConfig[] | undefined>({
  key: 'app.notificationSettings.filtered',
  get: ({ get }) => {
    const notificationSettings = get(editingNotificationSettingsState);
    if (!notificationSettings) return;
    let filteredNotificationSettings = [...notificationSettings];

    const search = get(searchNotificationSettingsState);

    if (search) {
      filteredNotificationSettings = filteredNotificationSettings.filter((notificationSetting) => {
        if (notificationSetting.notificationDescription?.toLowerCase().includes(search.toLowerCase())) return true;
        if (notificationSetting.categoryDisplay?.toLowerCase().includes(search.toLowerCase())) return true;
        return false;
      });
    }

    return filteredNotificationSettings;
  },
});

export const notificationSettingsByCategoriesState = selector<
  { category: string; categoryDisplay: string; settings: NotificationConfig[] | undefined }[] | undefined
>({
  key: 'app.notificationSettings.categories.by',
  get: ({ get }) => {
    const events = get(filteredNotificationSettingsState);
    if (!events) return;
    return Object.entries(APP_NOTIFICATION_CATEGORY_LABELS).map(([category, categoryDisplay]) => {
      return {
        category,
        categoryDisplay,
        settings: get(notificationSettingsInCategoryState(category)),
      };
    });
  },
});

export const notificationSettingsInCategoryState = selectorFamily<NotificationConfig[] | undefined, string>({
  key: 'app.notificationSettings.categories.in',
  get:
    (category) =>
    ({ get }) => {
      const events = get(filteredNotificationSettingsState);
      if (!events) return;
      return events.filter((event) => event.category === category);
    },
});

export const notificationSettingsCountState = selector<string>({
  key: 'app.client.notificationSettingsCount',
  get: ({ get }) => {
    const notificationsCount = get(notificationSettingsState)?.length ?? 0;
    const filteredCount = get(filteredNotificationSettingsState)?.length ?? 0;
    return `Mostrando ${filteredCount} de ${notificationsCount}`;
  },
});

export const searchNotificationSettingsState = atom({
  key: 'app.notificationSettings.search',
  default: '',
});

export const loadingLiveStatusSettingsState = atom({
  key: 'app.liveStatusSettings.loading',
  default: false,
});

export const liveStatusSettingsState = atom<LiveStatusConfig[] | undefined>({
  key: 'app.liveStatusSettings',
  default: undefined,
});

export const editingLiveStatusSettingsState = atom<LiveStatusConfig[] | undefined>({
  key: 'app.liveStatusSettings.editing',
  default: selector({
    key: 'app.liveStatusSettings.editing.initializer',
    get: ({ get }) => {
      return get(liveStatusSettingsState);
    },
  }),
});

export const updateLiveStatusSettingsState = atom<string | undefined>({
  key: 'app.liveStatusSettings.update',
  default: undefined,
});
